<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center p-2">
        <h1 class="text-2xl capitalize">
          {{ $t('ClaimCertifiedCosts.SharedFundCostModal.attachTitle') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full p-4 overflow-y-auto">
        <form
          action=""
          class="flex flex-col space-y-4"
          @submit.prevent="saveSharedFund"
        >
          <label>
            {{ $t('ClaimCertifiedCosts.SharedFundCostModal.currentClaim') }}
            <AppCurrencyInput
              v-model="sharedFundCostForm.form.current_claim"
              name="current_claim"
              type="text"
              class="form-input"
              :error="sharedFundCostForm.getErrors('current_claim')"
              data-test="current_claim"
            />
          </label>

          <label>
            {{ $t('ClaimCertifiedCosts.SharedFundCostModal.sharedFundCode') }}

            <AppSelect
              v-model.number="sharedFundCostForm.form.shared_fund_id"
              :options="availableSharedFunds.map((fund) => ({ value: fund.id, label: fund.code }))"
              name="shared_fund_id"
              data-test="shared_fund_id"
              class="form-input"
            />
          </label>

          <label>
            {{ $t('ClaimCertifiedCosts.SharedFundCostModal.sharedFundName') }}

            <AppSelect
              v-model.number="sharedFundCostForm.form.shared_fund_id"
              :options="availableSharedFunds.map((fund) => ({ value: fund.id, label: fund.name }))"
              name="shared_fund_id"
              data-test="shared_fund_name"
              class="form-input pr-8"
            />
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-center p-2 space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close'); sharedFundCostForm.resetData()"
        >
          {{ $t('ClaimCertifiedCosts.SharedFundCostModal.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="saveClaimAction.is(States.LOADING)"
          data-test="save-shared-fund"
          @click="saveSharedFund"
        >
          {{ $t('ClaimCertifiedCosts.SharedFundCostModal.attach') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useSignedData } from '@/composables/useSignedData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { parseUrlParams } from '@/helpers';
import Claim from '@/models/Claim';
import { useSharedFundCostsStore } from '@/store/sharedFundCosts';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { computed } from 'vue';

const props = defineProps<{
  claim: Claim;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const sharedFundCostsStore = useSharedFundCostsStore();
const sharedFundsStore = useSharedFundsStore();

const availableSharedFunds = computed(() => {
  const attachedFundIds = sharedFundCostsStore.models
    .where('claim_id', props.claim.id)
    .get()
    .map((cost) => cost.shared_fund_id);

  return sharedFundsStore.models
    .where('id', (id) => !attachedFundIds.includes(id))
    .get();
});

const sharedFundCostForm = useFormData({
  id: undefined,
  current_claim: 0,
  shared_fund_id: undefined,
});

const signedParams = useSignedData();
const saveClaimAction = useStoreApiAction(sharedFundCostsStore.saveClaim);

const claimUrlParams = computed(() => {
  return parseUrlParams(signedParams.meta.shared_fund_claim_url as string);
});

const saveSharedFund = () => {
  saveClaimAction.request(props.claim.id, claimUrlParams.value, sharedFundCostForm.form).then(() => {
    emits('close');
  }).catch((error) => {
    sharedFundCostForm.setErrors(error.data);
  });
};
</script>

<style scoped></style>
